import React from 'react';
import styled from '@emotion/styled';
import { ChevronDown, ChevronUp, CheckCircle2, Lock } from 'lucide-react';

/**
 * StepCard — one step of a pain point (the Kipupiste conversation, its
 * Peilaus) as a minimised sub-card: a header with the step's name and where
 * it stands, and the step itself under it only when opened. The two steps of
 * a pain point are long; minimised, both are in view at once and the person
 * knows where Peilaus is before scrolling to it.
 */

const Head = styled.button`
  all: unset;
  cursor: pointer;
  box-sizing: border-box;
  width: 100%;
  background: #141821;
  border: 1px solid #232a36;
  border-radius: 10px;
  padding: 0.95rem 1.35rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  ${(p) => p.$open && 'border-bottom-left-radius: 0; border-bottom-right-radius: 0; border-bottom-color: #1b2029;'}
  &:hover, &:focus-visible { border-color: #3a4457; }
  &:disabled { cursor: default; opacity: 0.6; }
  &:disabled:hover { border-color: #232a36; }
`;

const IconWrap = styled.div`
  flex: 0 0 auto;
  width: 2.2rem;
  height: 2.2rem;
  border-radius: 8px;
  display: grid;
  place-items: center;
  background: ${(p) => (p.$kind === 'kipupiste' ? 'rgba(239, 83, 80, 0.14)' : 'rgba(242, 193, 78, 0.14)')};
  color: ${(p) => (p.$kind === 'kipupiste' ? '#FF8A85' : '#f2c14e')};
`;

const Text = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
`;

const Status = styled.span`
  font-size: 0.85rem;
  font-weight: 600;
  color: ${(p) => (p.$tone === 'done' ? '#6fd3c8' : p.$tone === 'open' ? '#f2c14e' : '#7b8794')};
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
`;

const Chevron = styled.span`
  flex: 0 0 auto;
  color: #7b8794;
  display: inline-flex;
`;

// The step's own card sits flush under the header, as one piece.
const Body = styled.div`
  > *:first-of-type {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    border-top: 0;
  }
`;

/**
 * @param {object} props
 * @param {'kipupiste'|'peilaus'} props.kind
 * @param {string} props.title
 * @param {string} props.status what the step says about itself: Tehty, Kesken, …
 * @param {'done'|'open'|'idle'|'locked'} props.tone
 * @param {boolean} props.open
 * @param {() => void} props.onToggle
 */
export default function StepCard({ kind, icon: Icon, title, status, tone, open, onToggle, children }) {
  const locked = tone === 'locked';
  return (
    <div>
      <Head type="button" $open={open && !locked} onClick={onToggle} disabled={locked} aria-expanded={open && !locked}>
        <IconWrap $kind={kind}><Icon size={18} /></IconWrap>
        <Text>
          <strong>{title}</strong>
          <Status $tone={tone}>
            {tone === 'done' && <CheckCircle2 size={14} />}
            {locked && <Lock size={13} />}
            {status}
          </Status>
        </Text>
        {!locked && <Chevron>{open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}</Chevron>}
      </Head>
      {open && !locked && <Body>{children}</Body>}
    </div>
  );
}
